import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Star, Leaf, Eye, ShoppingCart, Heart } from "lucide-react";

const ProductImage = ({ src, alt, className, onLoad }) => {
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "/placeholder-image.jpg";
    // If it's already a base64 string, return it directly
    if (imagePath.startsWith("data:image")) return imagePath;
    // If it's a complete URL, return it directly
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
    // For server paths, construct the full URL
    return `${window.location.origin}/storage/${imagePath}`;
  };

  return (
    <div className="relative group">
      <img
        src={getImageUrl(src)}
        alt={alt}
        className={className}
        onError={(e) => {
          e.target.src = "/placeholder-image.jpg";
          e.target.onerror = null;
        }}
        onLoad={onLoad}
      />
      {/* Overlay on hover */}
      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-xl"></div>
    </div>
  );
};

const API_BASE_URL = "https://api.greenecobagsusa.com/api";

const ProductsGrid = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [minPrice, setMinPrice] = useState("0.00");
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleProducts, setVisibleProducts] = useState(3);
  const [hoveredProduct, setHoveredProduct] = useState(null);

  // Helper function to format price by removing trailing zeros
  const formatPrice = (price) => {
    // Convert to string first
    const priceStr = price.toString();
    
    // If it's not a valid number, return as is
    if (isNaN(parseFloat(priceStr))) {
      return priceStr;
    }
    
    // Parse as number to remove trailing zeros, then convert back to string
    const numPrice = parseFloat(priceStr);
    
    // If it's a whole number, add .00
    if (numPrice % 1 === 0) {
      return numPrice.toString() + '.00';
    }
    
    // For decimal numbers, parseFloat automatically removes trailing zeros
    return numPrice.toString();
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch products and sizes in parallel (same logic as ProductsPage)
      const [productsResponse, sizesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/products`),
        axios.get(`${API_BASE_URL}/sizes`)
      ]);

      if (!productsResponse.data || !productsResponse.data.data) {
        throw new Error("Aucune donnée reçue.");
      }

      // Find the minimum price from all sizes (same logic as ProductsPage)
      let lowestPrice = Number.MAX_VALUE;
      const sizesData = sizesResponse.data;

      if (Array.isArray(sizesData)) {
        sizesData.forEach(size => {
          const price = parseFloat(size.price);
          if (!isNaN(price) && price < lowestPrice) {
            lowestPrice = price;
          }
        });
      } else if (sizesData.data && Array.isArray(sizesData.data)) {
        sizesData.data.forEach(size => {
          const price = parseFloat(size.price);
          if (!isNaN(price) && price < lowestPrice) {
            lowestPrice = price;
          }
        });
      }

      // Set the global minimum price
      const globalMinPrice = lowestPrice !== Number.MAX_VALUE ? lowestPrice.toString() : "0.00";
      setMinPrice(globalMinPrice);

      // Correctly process the products to handle base64 images
      const processedProducts = productsResponse.data.data.map(product => ({
        ...product,
        // Correctly use the base64 string directly if it exists
        image_base64: product.image_base64 || null,
        // Keep the image_details path as a fallback
        image_details: product.image_details
          ? `${API_BASE_URL.replace('/api', '')}/storage/${product.image_details}`
          : null
      }));

      setProducts(processedProducts);
      setTotalItems(processedProducts.length);
      setError(null);
    } catch (error) {
      console.error("Error loading products:", error);
      setError(error.message || "Error loading products.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleProductClick = (id) => navigate(`/details/${id}`);

  if (loading) {
    return (
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="animate-spin h-16 w-16 rounded-full border-4 border-green-200 border-t-green-600 mx-auto"></div>
              <div className="absolute inset-0 animate-ping h-16 w-16 rounded-full border-4 border-green-300 opacity-20"></div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-700">Loading Amazing Products</h3>
              <p className="text-gray-500">Discovering the best eco-friendly options for you...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xl">!</span>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h3>
            <p className="text-gray-600 mb-8">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Leaf className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Products Available</h3>
            <p className="text-gray-600">We're working on adding amazing eco-friendly products. Check back soon!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full py-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-emerald-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Leaf className="w-4 h-4" />
            <span>Eco-Friendly Products</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our Best-Selling
            <span className="block bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Sustainable Products
            </span>
          </h2>
          
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto mb-8"></div>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our carefully curated collection of eco-friendly products that combine 
            sustainability with exceptional quality and style.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          {products.slice(0, visibleProducts).map((product, index) => {
            // Prioritize base64 image if available, otherwise use image_details path
            const imageSrc = product.image_base64 || product.image_details;

            return (
              <div
                key={product.id}
                className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden cursor-pointer"
                onClick={() => handleProductClick(product.id)}
                onMouseEnter={() => setHoveredProduct(product.id)}
                onMouseLeave={() => setHoveredProduct(null)}
                style={{
                  animationDelay: `${index * 150}ms`,
                  animation: 'fadeInUp 0.6s ease-out forwards'
                }}
              >
                {/* Product Image */}
                <div className="relative h-64 overflow-hidden">
                  <ProductImage
                    src={imageSrc}
                    alt={product.name}
                    className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Action Buttons */}
                  <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0">
                    <button className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110">
                      <Heart className="w-5 h-5 text-gray-600 hover:text-red-500" />
                    </button>
                    <button className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110">
                      <Eye className="w-5 h-5 text-gray-600 hover:text-green-500" />
                    </button>
                  </div>

                  {/* Eco Badge */}
                  <div className="absolute top-4 left-4">
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                      <Leaf className="w-3 h-3" />
                      <span>Eco</span>
                    </div>
                  </div>
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">(4.9)</span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300">
                    {product.name}
                  </h3>

                  <div className="flex items-center justify-between mb-4">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-bold text-green-600">${formatPrice(minPrice)}</span>
                    <span className="text-sm text-gray-500">starting from</span>
                  </div>
                  </div>

                  <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform group-hover:scale-105 flex items-center justify-center space-x-2">
                    <span>View Details</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>

                {/* Hover Effect Border */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-200 rounded-2xl transition-colors duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        {products.length > visibleProducts && (
          <div className="text-center mt-16">
            <button
              onClick={() => navigate("/products")}
              className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <ShoppingCart className="w-5 h-5" />
                <span>View All Products</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default ProductsGrid;